import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action, set } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { restartableTask, timeout } from 'ember-concurrency';
import { inject } from '@ember/service';
import { nuovaOwnsPageScroll } from '../lib/nv-scroll-restore.js';

var _class, _descriptor, _descriptor2;

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      Foundation ROOT base class for `*-detail`/`*-edit`/scuola/guide
 *              components fleet-wide (mega-cluster root)
 *   extends:   Component (@glimmer/component) — plain Glimmer Octane base, no
 *              Classic mixin
 *   template:  none (TEMPLATELESS — each subclass owns its own template)
 *   args:      @item, @applicationController, @wrapIsEditable, @documentType
 *   uses:      @ember/object (action, set), @ember/object/internals (guidFor),
 *              ember-concurrency (restartableTask, timeout), session service
 *   siblings:  -
 *   tests:     tests/unit/components/base-beebee-component-test.js,
 *              tests/unit/components/base-beebee-component-wave2-test.js,
 *              tests/integration/components/base-beebee-component-test.js
 *   gotchas:   subclass templates MUST wire `{{did-insert this.baseSetup}}` on
 *              the root element (replaces Classic `didInsertElement` scroll);
 *              `editItem` mutates `applicationController.modalComponents` via
 *              `set()` (a Classic controller) — plain array assignment would
 *              not notify the template.
 *
 * Glimmer Octane ROOT base for every `*-detail`/`*-edit`/scuola/guide component
 * (mega-cluster root; GLIMMER-OCTANE-MIGRATION-PLAN §10 Layer 1). Replaces the
 * Classic `base-beebee` MIXIN → Classic `.extend` base (mixin-removal 1.4/3.1).
 * Role/state getters off the session + edit/reload actions.
 *
 * TEMPLATELESS: this base has NO template of its own. Each subclass owns its
 * template and is curly- or angle-invoked with `@item` / `@applicationController`
 * / `@wrapIsEditable` / `@documentType` etc. — all read here via `this.args.*`.
 *
 * SUBCLASS WIRING CONTRACT (Layer 2/3 templates MUST add these to their root):
 *   - `{{did-insert this.baseSetup}}` — runs the scroll-top animate task
 *     (replaces the mixin's `didInsertElement` scroll). The modifier supplies the
 *     DOM element; NEVER `this.element`.
 *   - `elementId` is now a getter (`guidFor(this)`); templates relying on the
 *     wrapper id keep using `{{this.elementId}}` (still stable & unique).
 *   - actions: `{{this.reload}}` / `{{this.editItem}}` (were `{{action 'reload'}}`
 *     / `{{action 'editItem'}}`).
 *   - getters unchanged by name: `isEditable`, `isArchived`, `userRole`,
 *     `contactStates`, `contactStateCliente`, `contactStateFornitore`,
 *     `isContactsEditable`/`isProjectsEditable`/`isProductsEditable`/
 *     `isWorkhoursEditable`/`isPaymentsEditable`/`isEventsEditable`/
 *     `isFileEditable`/`isExpensesEditable`/`isInvoicesEditable`.
 */
let BaseBeebeeComponent = (_class = class BaseBeebeeComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "intl", _descriptor, this);
    _initializerDefineProperty(this, "session", _descriptor2, this);
  }
  get elementId() {
    return guidFor(this);
  }
  *animateTask(element) {
    yield timeout(500);
    const parentElement = element?.closest('.scroll-top-on-insert');
    if (parentElement && !nuovaOwnsPageScroll(parentElement)) {
      parentElement.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Subclass templates wire this via `{{did-insert this.baseSetup}}`; the
   * modifier passes the root DOM element (was the mixin's `didInsertElement`).
   */
  baseSetup(element) {
    this.animateTask.perform(element);
  }
  get contactStates() {
    return this.session.contactStates;
  }
  findContactState(statusName) {
    return this.contactStates?.find(s => s.status_name === statusName);
  }
  get contactStateCliente() {
    return this.findContactState('Clienti');
  }
  get contactStateFornitore() {
    return this.findContactState('Fornitori');
  }
  get userRole() {
    return this.session.me?.role || {};
  }
  get isEditable() {
    if (this.args.wrapIsEditable !== undefined) {
      return this.args.wrapIsEditable;
    }
    const modelName = this.args.item?.constructor?.modelName;
    const role = this.userRole;
    // userRole falls back to a plain {} when me/role is missing (no Ember
    // `.get`); guard it to avoid a TypeError while role is unavailable.
    if (typeof role?.get !== 'function') {
      return false;
    }
    return role.get(`${modelName}_edit`) || false;
  }
  get isArchived() {
    return !!this.args.item?.archived;
  }
  get isContactsEditable() {
    return this._checkRole('contacts');
  }
  get isProjectsEditable() {
    return this._checkRole('projects');
  }
  get isProductsEditable() {
    return this._checkRole('products');
  }
  get isWorkhoursEditable() {
    return this._checkRole('workhours');
  }
  get isPaymentsEditable() {
    return this._checkRole('payments');
  }
  get isEventsEditable() {
    return this._checkRole('events');
  }
  get isFileEditable() {
    return this._checkRole('file');
  }
  get isExpensesEditable() {
    return this._checkRole('expenses');
  }
  get isInvoicesEditable() {
    return this._checkRole('invoices');
  }

  /**
   * Role-based editability for the item's model (estimate override for
   * estimate-flavoured invoices). Mirrors the mixin's `_checkRole` — including
   * its existing quirk of ignoring the passed model name and always using the
   * item's own model name.
   */
  _checkRole() {
    const modelName = this.args.item?.constructor?.modelName;
    const role = this.userRole;
    if (!modelName || !role) {
      return false;
    }
    if (modelName === 'invoice' && this.args.item?.estimate) {
      return role.estimate_edit;
    }
    // role is a plain {} when me/role is missing (no Ember `.get`); guard it.
    if (typeof role.get !== 'function') {
      return false;
    }
    return role.get(`${modelName}_edit`);
  }
  reload() {
    this.args.item?.reload();
  }
  editItem() {
    const modelName = this.args.item?.constructor?.modelName;
    const rName = modelName === 'invoice' ? this.args.documentType : modelName;
    const organization = this.session.me?.organization;
    const editComponentName = organization?.get(`${rName}_name`);
    const modalComponents = [...(this.args.applicationController?.modalComponents || [])];
    modalComponents.push({
      title: `${this.intl.t('edit')} ${editComponentName}`,
      componentName: this.session.me?.role?.application?.get(`${modelName}_edit`),
      item: this.args.item
    });

    // applicationController is a Classic controller; modalComponents is read
    // reactively by application.hbs. Plain assignment of the new array would not
    // notify (the edit modal never opened) → set() restores reactivity.
    set(this.args.applicationController, 'modalComponents', modalComponents);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "animateTask", [restartableTask], Object.getOwnPropertyDescriptor(_class.prototype, "animateTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "baseSetup", [action], Object.getOwnPropertyDescriptor(_class.prototype, "baseSetup"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "reload", [action], Object.getOwnPropertyDescriptor(_class.prototype, "reload"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "editItem", [action], Object.getOwnPropertyDescriptor(_class.prototype, "editItem"), _class.prototype), _class);

export { BaseBeebeeComponent as default };
//# sourceMappingURL=base-beebee-component.js.map
